'use client';

import { useMemo } from 'react';
import { useQueryHook } from '@/lib/query/use-query-hook';
import type { QueryResult } from '@/types/async-protocol';

const IMAGE_LOAD_ABORT_MESSAGE = 'Image load aborted.';
const IMAGE_LOAD_ABORT_ERROR_NAME = 'AbortError';
const IMAGE_LOAD_FAILED_MESSAGE = 'Image load failed.';
const IMAGE_DECODING_ASYNC = 'async';

export function loadImage(src: string, signal?: AbortSignal): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    const cleanup = () => {
      signal?.removeEventListener('abort', abort);
      image.onload = null;
      image.onerror = null;
    };
    const abort = () => {
      cleanup();
      reject(new DOMException(IMAGE_LOAD_ABORT_MESSAGE, IMAGE_LOAD_ABORT_ERROR_NAME));
    };
    image.onload = () => {
      cleanup();
      resolve(image);
    };
    image.onerror = () => {
      cleanup();
      reject(new Error(IMAGE_LOAD_FAILED_MESSAGE));
    };
    signal?.addEventListener('abort', abort, { once: true });
    image.decoding = IMAGE_DECODING_ASYNC;
    image.src = src;
  });
}

export function useImageLoad(src: string): QueryResult<HTMLImageElement> {
  return useQueryHook<HTMLImageElement, Error>(
    useMemo(
      () => ({
        queryKey: ['files', 'image-load', src],
        queryFn: ({ signal }) => loadImage(src, signal),
        options: { enabled: Boolean(src) },
      }),
      [src],
    ),
  );
}
